/**
 * Is this prompt an instruction to edit the question in front of us?
 *
 * Leo's draft pipeline reads the prompt two ways. Normally the prompt
 * describes what the question should be *about*, so the words in it
 * become the topic. But the Leo spotted cards send directives — "rewrite
 * only the stem", "the rationale is missing, write it" — which say what
 * to change, not what to write about.
 *
 * Told apart wrongly, the directive itself becomes the subject, and the
 * author gets back a question whose stem is the instruction they just
 * pressed: *"Which statement best explains … The rationale is missing.
 * Write it: explain why the key is correct …?"* with the same sentence
 * repeated down the answer choices. That is what this exists to stop.
 *
 * Every phrasing the cards can send is listed here. When a card grows a
 * new instruction it must be added, which is why the table that emits
 * them — `DIMENSION_INSTRUCTION` in `leo-spotted-queue` — points back at
 * this file.
 */
const EDIT_DIRECTIVE =
  /resolve all open findings|rewrite this question to resolve|^rewrite only the (stem|answer choices)\b|^rewrite the stem and the answer choices together\b|^write a fresh variant of this question\b|^add the reference the stem points at\b|^diagnose why this question\b|^the rationale is missing\b|^add a rationale\b|^suggest the (bloom|exam category)/i

/**
 * True when the prompt tells Leo what to change rather than what the
 * question should be about.
 */
export function isLeoQuestionEditDirective(prompt: string): boolean {
  const text = prompt.replace(/\s+/g, " ").trim()
  return EDIT_DIRECTIVE.test(text) || leoAskedScope(text) !== null
}

export type LeoAskedScope = "rationale" | "stem" | "choices"

function normalizedAsk(prompt: string): string {
  return prompt.replace(/\s+/g, " ").trim()
}

/** The ask names a stem change and does not also ask to rewrite choices. */
function asksStemAlone(text: string): boolean {
  const lower = text.toLowerCase()
  if (/\b(?:and|replace|with) (?:those |the |that )?(?:two )?choices\b/.test(lower)) return false
  if (/\breplace .+\bchoices\b/.test(lower)) return false
  if (/\bdifficulty\b/.test(lower) && /\b(?:set|and)\b/.test(lower)) return false
  return (
    /\bstem only\b|\brewrite only the stem\b|\bfix this in the stem\b|\bfinish the stem only\b|\bthe stem only\b|\btighten the stem\b/.test(lower) ||
    /^rewrite the stem so\b/.test(lower) ||
    /^rewrite the stem without\b/.test(lower) ||
    /^mark not\b/.test(lower) ||
    /^give me another stem\b/.test(lower) ||
    /^give me a harder stem\b/.test(lower) ||
    /^give me a new wording only\b/.test(lower) ||
    /^shorten the essay\b/.test(lower) ||
    /^finish the stem only\b/.test(lower)
  )
}

/** The ask names a choice change and does not also ask to rewrite the stem. */
function asksChoicesAlone(text: string): boolean {
  const lower = text.toLowerCase()
  if (/\b(?:rewrite|fix|finish|tighten|complete|change)(?: the)? stem\b|\bstem only\b/.test(lower)) {
    return false
  }
  return (
    /\b(?:choices|distractors|options) only\b|\brewrite only the answer choices\b|\bchange the choices only\b|\bfix this in the answer choices\b|\breplace\b.+\b(?:distractors|choices)\b|\bkeep every retained choice\b/.test(lower)
  )
}

/**
 * Which single field this instruction is allowed to change.
 *
 * "Leave the answer choices" is a stem edit, not a request to rewrite
 * the choices. "Fix the stem and the choices only" names two fields, so
 * it is not a choices-only ask. Matching "choices only" anywhere in that
 * sentence is how a stem-and-choices ask came back as choices alone.
 */
export function leoAskedScope(prompt: string): LeoAskedScope | null {
  const text = normalizedAsk(prompt)
  if (!text) return null
  const lower = text.toLowerCase()
  const rationaleLead =
    /^(?:the )?rationale is missing\b/.test(lower) ||
    /^(?:write|add) (?:a |the )?rationale\b/.test(lower) ||
    /rationale:\s*why the keyed answer/i.test(text) ||
    /^help me add a rationale\b/.test(lower)
  if (rationaleLead && !/\bfix the stem\b/.test(lower)) return "rationale"
  if (asksStemAlone(text)) return "stem"
  if (asksChoicesAlone(text)) return "choices"
  return null
}

/**
 * Replace All of the above / None of the above in place.
 *
 * This is a choice edit, not a request for a new question. The cards send
 * it as one sentence. Routing it as an open rewrite asks the model for a
 * full labeled draft, and a draft that fails the type check comes back as
 * "no draft was added" instead of new choice text.
 */
export function isRetainedChoiceRewrite(prompt: string): boolean {
  const text = prompt.replace(/\s+/g, " ").trim()
  if (!text) return false
  return (
    /replace all of the above and none of the above with real distractors/i.test(
      text,
    ) || /keep every retained choice in its current letter/i.test(text)
  )
}
